// Read-only adapter for the shop-owned Kaching configuration. Cart discounts
// remain entirely owned by Kaching; this only displays the one-unit offer.
const id = value => String(value ?? '').split('/').pop();
const includes = (values, value) => (values || []).some(item => id(item) === id(value));
const intersects = (values, candidates) => candidates.some(value => includes(values, value));

export function findOffer(deals, context) {
  const collections = context.collectionIds || [];
  return (Array.isArray(deals) ? deals : []).filter(deal => {
    if (deal.enabled === false || deal.excludeB2bCustomers && context.b2b) return false;
    if (deal.marketId && id(deal.marketId) !== id(context.marketId)) return false;
    if (includes(deal.excludedMarketIds, context.marketId) || includes(deal.excludedProductIds, context.productId) || intersects(deal.excludedCollectionIds, collections)) return false;
    switch (deal.blockVisibility) {
      case 'all-products': return true;
      case 'selected-products': return includes(deal.selectedProductIds, context.productId);
      case 'selected-collections': return intersects(deal.selectedCollectionIds, collections);
      default: return false;
    }
  }).sort((a, b) => Number(Boolean(b.marketId)) - Number(Boolean(a.marketId)))[0];
}

export function roundOffer(price, precision = '.00') {
  switch (precision) {
    case '.99': return price > 99 ? 100 * Math.ceil(price / 100) - 1 : price;
    case '.95': return price > 95 ? 100 * Math.ceil(price / 100) - 5 : price;
    case '.90': return price > 90 ? 100 * Math.ceil(price / 100) - 10 : price;
    case '.x9': return price > 9 ? 10 * Math.ceil(price / 10) - 1 : price;
    case '.x0': return 10 * Math.ceil(price / 10);
    case '.00': return 100 * Math.ceil(price / 100);
    default: return null;
  }
}

export function singleOfferPrice(deal, product, rate) {
  const bar = deal?.dealBars?.find(bar => Number(bar.quantity) === 1 && bar.dealBarType === 'quantity-break');
  if (!bar || deal.currency || deal.subscriptionsEnabled || deal.scratchOffEnabled || deal.collectionBreaksEnabled || deal.progressiveGiftsEnabled || deal.checkboxUpsellsEnabled || bar.quantitySelector || bar.sellingPlanEnabled || bar.hideBehindScratchOff || bar.showAsSoldOutEnabled || bar.freeGifts?.length || bar.multipleGiftsSelectors?.length || bar.upsells?.length || bar.productPersonalisation) return null;
  const base = Number(product.price);
  const value = Number(bar.discountValue);
  if (!Number.isFinite(base) || !Number.isFinite(value)) return null;
  let price;
  switch (bar.discountType) {
    case 'default': price = base; break;
    case 'percentage': price = Math.ceil(base * (100 - Math.max(0, Math.min(100, value))) / 100); break;
    case 'amount':
    case 'specific':
      if (!(rate > 0)) return null;
      price = bar.discountType === 'specific' ? value * 100 * rate : base - Math.round(value * 100) * rate;
      break;
    default: return null;
  }
  price = Math.max(0, Math.min(base, price));
  if (deal.priceRounding && bar.discountType !== 'default') {
    const rounded = roundOffer(price, deal.priceRoundingPrecision);
    if (rounded === null) return null;
    if (rounded <= base) price = rounded;
  }
  return { price: Math.round(price), compare: deal.useProductCompareAtPrice ? Math.max(base, Number(product.compareAt) || 0) : base, offerId: deal.nanoId || deal.id };
}

export function formatCardMoney(amount, format, withoutDecimals = false) {
  const token = format?.match(/\{\{\s*(\w+)\s*\}\}/);
  if (!token) return null;
  const key = token[1];
  const commaDecimal = key.includes('comma_separator');
  const spaceThousands = key.includes('space_separator');
  const apostropheThousands = key.includes('apostrophe_separator');
  const decimals = withoutDecimals || key.includes('no_decimals') ? 0 : 2;
  const parts = (amount / 100).toFixed(decimals).split('.');
  const separator = spaceThousands ? ' ' : apostropheThousands ? "'" : commaDecimal ? '.' : ',';
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return format.replace(token[0], parts.join(commaDecimal ? ',' : '.'));
}

if (typeof window !== 'undefined' && !customElements.get('kaching-card-price')) {
  customElements.define('kaching-card-price', class extends HTMLElement {
    connectedCallback() {
      // Section filtering and recommendations insert new elements after load.
      queueMicrotask(() => this.update());
    }
    update() {
      const source = document.querySelector('[data-kaching-card-config]');
      const data = this.querySelector('[data-card-pricing]');
      if (!source || !data) return;
      let config, product;
      try { config = JSON.parse(source.textContent); product = JSON.parse(data.textContent); } catch { return; }
      const offer = findOffer(config.deals, { ...config, ...product });
      if (!offer) return;
      const rate = config.currency === config.shopCurrency ? 1 : Number(window.Shopify?.currency?.rate);
      const result = singleOfferPrice(offer, product, rate);
      // Unsupported/gated offers retain Shopify pricing, never the old manual discount.
      const { price, compare } = result || { price: product.price, compare: product.compareAt };
      const money = amount => formatCardMoney(amount, config.moneyFormat, offer.showPricesWithoutDecimals) || new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: config.currency
      }).format(amount / 100);
      const sale = this.querySelector('sale-price');
      if (!sale) return;
      const amount = sale.querySelector('[data-performance-set-add-price-suffix]') || sale;
      const formatted = money(price);
      amount.textContent = product.priceVaries ? config.fromLabel.replace('[price]', formatted) : formatted;
      sale.classList.toggle('text-on-sale', compare > price);
      sale.classList.toggle('text-subdued', !(compare > price));
      let compareNode = this.querySelector('compare-at-price');
      if (!compareNode) {
        compareNode = document.createElement('compare-at-price');
        compareNode.className = 'text-subdued line-through';
        sale.after(compareNode);
      }
      compareNode.textContent = compare > price ? money(compare) : '';
      compareNode.hidden = !(compare > price);
      let savings = this.querySelector('.product-card__savings');
      if (!savings && product.showBadge && config.showDiscount && compare > price) {
        savings = document.createElement('div');
        savings.className = 'product-card__savings';
        const badge = document.createElement('on-sale-badge');
        badge.className = 'badge badge--on-sale';
        savings.append(badge);
        this.append(savings);
      }
      if (savings) {
        savings.hidden = !(compare > price) || !product.showBadge || !config.showDiscount;
        const value = config.discountMode === 'percentage' ? `${Math.round((compare - price) / compare * 100)}%` : money(compare - price);
        savings.querySelector('on-sale-badge').textContent = config.savingsLabel.replace('[savings]', value);
      }
      this.dataset.offerId = result?.offerId || '';
    }
  });
}
